import { ArdoqId } from '@ardoq/api-types';
import { GraphModelShape } from '@ardoq/data-model';
import { ReferenceFilterContext } from '@ardoq/filter-helpers';
import {
  getOnlyConnectedComponents,
  GraphInterface,
  GraphTraversalResult,
  traverseGraph,
  TraverseOptions,
} from '@ardoq/graph';
import { uniq } from 'lodash';

type SequentialTraversalReducerAggregate = {
  componentIds: ArdoqId[];
  traversedReferenceIds: ArdoqId[];
  parentReferenceIds: Set<string>;
  previousTraversedComponentIds: Set<ArdoqId>;
};
const sequentialTraversalReducerCurry =
  (
    graphInterface: GraphInterface,
    graph: GraphModelShape,
    ctx: ReferenceFilterContext
  ) =>
  (
    {
      componentIds,
      traversedReferenceIds,
      parentReferenceIds,
      previousTraversedComponentIds,
    }: SequentialTraversalReducerAggregate,
    sequentialTraverseOptions: TraverseOptions
  ) => {
    const {
      componentIds: newComponentIds,
      traversedReferenceIds: newTraversedReferenceIds,
      parentReferenceIds: newParentReferenceIds,
    } = traverseGraph(
      graphInterface,
      componentIds.filter(
        componentId => !previousTraversedComponentIds.has(componentId)
      ),
      graph,
      sequentialTraverseOptions,
      ctx
    );

    return {
      componentIds: uniq([...componentIds, ...newComponentIds]),
      traversedReferenceIds: [
        ...traversedReferenceIds,
        ...newTraversedReferenceIds,
      ],
      parentReferenceIds: new Set([
        ...parentReferenceIds,
        ...newParentReferenceIds,
      ]),
      previousTraversedComponentIds: new Set([
        ...previousTraversedComponentIds,
        ...componentIds,
      ]),
    };
  };

type GetComponentAndreferenceIdsForWorkspaceModeArgs = {
  graphInterface: GraphInterface;
  startSet: ArdoqId[];
  graph: GraphModelShape;
  traverseOptions: TraverseOptions;
  sequentialTraverseOptions: TraverseOptions[];
  includeOnlyConnectedComponents: boolean;
  ctx: ReferenceFilterContext;
};

export const getTraversalResultForWorkspaceMode = ({
  graphInterface,
  startSet,
  graph,
  includeOnlyConnectedComponents,
  traverseOptions,
  sequentialTraverseOptions,
  ctx,
}: GetComponentAndreferenceIdsForWorkspaceModeArgs): GraphTraversalResult => {
  const traverseResult = traverseGraph(
    graphInterface,
    startSet,
    graph,
    traverseOptions,
    ctx
  );

  const {
    componentIds: allComponentIds,
    traversedReferenceIds,
    parentReferenceIds,
  } = sequentialTraverseOptions.reduce<SequentialTraversalReducerAggregate>(
    sequentialTraversalReducerCurry(graphInterface, graph, ctx),
    { ...traverseResult, previousTraversedComponentIds: new Set() }
  );

  const componentIds = includeOnlyConnectedComponents
    ? getOnlyConnectedComponents(
        allComponentIds,
        traversedReferenceIds,
        parentReferenceIds,
        graph.referenceMap
      )
    : allComponentIds;

  return { componentIds, traversedReferenceIds, parentReferenceIds };
};
